import { Form } from "react-bootstrap";
import { Controller } from "react-hook-form";
import PropTypes from "prop-types";

// Helper: Generate number range
const generateOptions = (start, end, step, decimals = 2) => {
  const options = [];
  let current = start;
  while (current <= end) {
    options.push(current.toFixed(decimals));
    current += step;
  }
  return options;
};

const OpticalDetailsTable = ({ control, watch, setValue }) => {
  const productType = watch("productType");
  const isContact = productType === "contact";
  const isLens = productType === "lens";

  let fields = [];

  if (isLens) {
    fields = [
      { label: "SPHERE *", name: "sph", type: "single-select" },
      { label: "CYLINDER", name: "cyl", type: "single-select" },
      { label: "AXIS", name: "axis", type: "single-select" },
      { label: "IN", name: "in", type: "single-select" },
      { label: "OUT", name: "out", type: "single-select" },
      { label: "DOWN", name: "down", type: "single-select" },
      { label: "UP", name: "up", type: "single-select" },
      { label: "ADD", name: "add", type: "single-select" },
      { label: "COLOR", name: "color", type: "text" },
      { label: "BRAND", name: "brand", type: "text" },
      { label: "PD", name: "pd", type: "text", side: "left" },
    ];
  } else if (isContact) {
    fields = [
      { label: "POWER *", name: "power", type: "single-select" },
      { label: "CYLINDER", name: "cyl", type: "single-select" },
      { label: "AXIS", name: "axis", type: "single-select" },
      { label: "BACKCURVE", name: "backcurve", type: "text" },
      { label: "DIAMETER", name: "diameter", type: "text" },
      { label: "ADD", name: "add", type: "single-select" },
      { label: "COLOR", name: "color", type: "text" },
      { label: "BRAND", name: "brand", type: "text" },
      { label: "DURATION", name: "duration", type: "duration" },
      { label: "PD", name: "pd", type: "text", side: "left" },
    ];
  } else {
    fields = [
      { label: "IN", name: "in" },
      { label: "OUT", name: "out" },
      { label: "DOWN", name: "down" },
      { label: "UP", name: "up" },
    ];
  }

  const getOptions = (fieldName, sideType) => {
    switch (fieldName) {
      case "sph":
        return generateOptions(-20.0, 20.0, 0.25);
      case "power":
        return generateOptions(-12.0, 12.0, 0.25);
      case "cyl":
      case "add":
        return generateOptions(-12.0, 12.0, 0.25);
      case "axis":
        return generateOptions(1, 180, 1, 0);
      case "in":
      case "out":
      case "down":
      case "up":
        return generateOptions(0.0, 10.0, 0.5);
      case "backcurve":
        return generateOptions(4.0, 8.0, 0.1, 1);
      case "diameter":
        return generateOptions(5.0, 30.0, 0.1, 1);
      default:
        return [];
    }
  };

  const renderSelect = (name) => {
    const [side, fieldName, sideType] = name.split("_"); // Extract parts
    const options = getOptions(fieldName, sideType); // Pass both

    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Form.Select {...field} className="fs-10">
            <option value="">Select</option>
            {options.map((val) => (
              <option key={val} value={val}>
                {val}
              </option>
            ))}
          </Form.Select>
        )}
      />
    );
  };

  const renderTextInput = (name) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Form.Control {...field} type="text" className="fs-10" placeholder="" />
      )}
    />
  );

  const renderDuration = (name) => (
    <div className="d-flex gap-2">
      <Controller
        name={`${name}_value`}
        control={control}
        render={({ field }) => (
          <Form.Control
            {...field}
            type="number"
            className="fs-10"
            placeholder=""
          />
        )}
      />
      <Controller
        name={`${name}_unit`}
        control={control}
        render={({ field }) => (
          <Form.Select
            {...field}
            className="fs-10"
            onChange={(e) => setValue(`${name}_unit`, e.target.value)}
          >
            <option value="">Select</option>
            <option value="D">Days</option>
            <option value="WK">Weeks</option>
            <option value="MO">Months</option>
          </Form.Select>
        )}
      />
    </div>
  );

  return (
    <div className="table-responsive">
      <table className="table align-middle text-center">
        <thead>
          <tr>
            <th></th>
            <th colSpan={2} className="text-success">
              Left
            </th>
            <th colSpan={2} className="text-success">
              Right
            </th>
          </tr>
          <tr>
            <th></th>
            <th>Distance</th>
            <th>Near</th>
            <th>Distance</th>
            <th>Near</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => (
            <tr key={field.name}>
              <td>
                <strong className="fs-10 capitalize">{field.label}</strong>
              </td>
              {[
                "sph",
                "power",
                "cyl",
                "axis",
                "in",
                "up",
                "down",
                "out",
                "backcurve",
                "diameter",
              ].includes(field.name) ? (
                <>
                  <td>{renderSelect(`left_${field.name}_distance`)}</td>
                  <td>{renderSelect(`left_${field.name}_near`)}</td>
                  <td>{renderSelect(`right_${field.name}_distance`)}</td>
                  <td>{renderSelect(`right_${field.name}_near`)}</td>
                </>
              ) : field.name === "add" ? (
                <>
                  <td colSpan="2">{renderSelect(`left_${field.name}`)}</td>
                  <td colSpan="2">{renderSelect(`right_${field.name}`)}</td>
                </>
              ) : field.type === "text" && field.side === "left" ? (
                <>
                  <td colSpan="2">{renderTextInput(`left_${field.name}`)}</td>
                  <td colSpan="2"></td>
                </>
              ) : field.type === "text" ? (
                <>
                  <td colSpan="2">{renderTextInput(`left_${field.name}`)}</td>
                  <td colSpan="2">{renderTextInput(`right_${field.name}`)}</td>
                </>
              ) : field.name === "duration" ? (
                <>
                  <td colSpan="2">{renderDuration(`left_${field.name}`)}</td>
                  <td colSpan="2">{renderDuration(`right_${field.name}`)}</td>
                </>
              ) : field.type === "single-select" ? (
                <>
                  <td colSpan="2">{renderSelect(`left_${field.name}`)}</td>
                  <td colSpan="2">{renderSelect(`right_${field.name}`)}</td>
                </>
              ) : (
                <>
                  <td>{renderSelect(`left_${field.name}_distance`)}</td>
                  <td>{renderSelect(`left_${field.name}_near`)}</td>
                  <td>{renderSelect(`right_${field.name}_distance`)}</td>
                  <td>{renderSelect(`right_${field.name}_near`)}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

OpticalDetailsTable.propTypes = {
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
};

export default OpticalDetailsTable;
